import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, getShortDayNames, formatSEK, formatNumber } from '../../lib/format-utils';
import { weekSummary } from './week-summary';
import { useAuth } from '../../context/AuthContext';
import UnifiedAddDialog from '../../components/unified-add-dialog';
import SubmissionHistorySection from '../../components/submission-history-section';
import {
    getMonthInfo,
    getMonthSummary,
    getProjects,
    getDeviationTypes,
    getTimesheetStatus,
    submitTimesheet,
    submitExpenseReport,
    requestTimesheetReopen,
    getReopenRequests,
    getFlexBalance,
    getVacationBalance,
    bulkDeleteTimeEntries,
    bulkDeleteExpenses,
    getExpenseSettings,
    getExpenseReport,
    requestExpenseReopen,
    getExpenseReopenRequests,
    lockCalendarWeek,
    unlockCalendarWeek,
    lockCalendarMonth,
    unlockCalendarMonth,
    saveTimesheetNotes,
} from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getQuickCaptureFile, clearQuickCaptureFile } from '../../lib/quick-capture-store';
import { Plus, AlertCircle, Send, Lock, Unlock, Car, RefreshCw, CheckSquare, Square, Receipt, Hotel, Clock, Trash2, TrendingUp, FileText, Check, X } from 'lucide-react';
import MonthNavigator from '../../components/month-navigator';

// Week column wide enough for the week summary; weekends at half width since
// they are rarely logged on, giving the five working days the room.
const CALENDAR_COLUMNS = '4rem repeat(5, minmax(0, 1fr)) repeat(2, minmax(0, 0.5fr))';
const WEEK_SUMMARY_TONE = {
    even: 'bg-background text-foreground border-border',
    short: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800',
    over: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-800',
};
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';


// Friendly display names for receipt_type keys. Mirrored from the form's
// RECEIPT_TYPES list in unified-add-dialog.jsx. (Consolidation tracked as
// Phase 5 5.9 — move to a shared constants module or fetch from the backend.)
const RECEIPT_TYPE_LABELS = {
    travel_flight: 'Travel - Flights',
    travel_train: 'Travel - Train/Bus',
    travel_taxi: 'Travel - Taxi/Uber',
    travel_rental: 'Travel - Rental Car',
    travel_fuel: 'Travel - Fuel',
    travel_parking: 'Travel - Parking',
    travel_tolls: 'Travel - Tolls',
    accommodation: 'Accommodation',
    meals_client: 'Meals - Client',
    meals_business: 'Meals - Business',
    office_supplies: 'Office Supplies',
    software: 'Software/Subscriptions',
    equipment: 'Equipment',
    conference: 'Conference/Training',
    phone_internet: 'Phone/Internet',
    other: 'Other',
};

// Helper to get ISO week number
const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

export default function LogReports() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [month, setMonth] = useState(() => {
        const targetMonth = sessionStorage.getItem('targetMonth');
        if (targetMonth) {
            sessionStorage.removeItem('targetMonth');
            return parseInt(targetMonth);
        }
        return new Date().getMonth() + 1;
    });
    const [year, setYear] = useState(() => {
        const targetYear = sessionStorage.getItem('targetYear');
        if (targetYear) {
            sessionStorage.removeItem('targetYear');
            return parseInt(targetYear);
        }
        return new Date().getFullYear();
    });
    const [monthInfo, setMonthInfo] = useState(null);
    const [entries, setEntries] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [projects, setProjects] = useState([]);
    const [deviationTypes, setDeviationTypes] = useState([]);
    const [timesheetStatus, setTimesheetStatus] = useState(null);
    const [expenseReportStatus, setExpenseReportStatus] = useState(null);
    const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
    const [reopenReason, setReopenReason] = useState('');
    const [requestingReopen, setRequestingReopen] = useState(false);
    const [pendingReopenRequest, setPendingReopenRequest] = useState(null);
    const [expenseReopenDialogOpen, setExpenseReopenDialogOpen] = useState(false);
    const [expenseReopenReason, setExpenseReopenReason] = useState('');
    const [requestingExpenseReopen, setRequestingExpenseReopen] = useState(false);
    const [pendingExpenseReopenRequest, setPendingExpenseReopenRequest] = useState(null);

    // Notes state for this monthly time report
    const [notesOpen, setNotesOpen] = useState(false);
    const [notesText, setNotesText] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    const [notesSaved, setNotesSaved] = useState(false);
    const [notesDirty, setNotesDirty] = useState(false);
    const notesDirtyRef = useRef(false);
    const notesSavingRef = useRef(false);
    const notesTextRef = useRef('');
    const currentMonthRef = useRef(month);
    const currentYearRef = useRef(year);

    useEffect(() => {
        notesDirtyRef.current = notesDirty;
    }, [notesDirty]);

    useEffect(() => {
        notesSavingRef.current = notesSaving;
    }, [notesSaving]);

    useEffect(() => {
        notesTextRef.current = notesText;
    }, [notesText]);

    useEffect(() => {
        currentMonthRef.current = month;
        currentYearRef.current = year;
    }, [month, year]);

    // Bulk selection state
    const [bulkSelectMode, setBulkSelectMode] = useState(false);
    const [selectedDays, setSelectedDays] = useState([]);

    // Bulk delete state
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkDeleteCategories, setBulkDeleteCategories] = useState({ time: false, mileage: false, receipt: false, allowance: false });
    const [bulkDeleting, setBulkDeleting] = useState(false);

    // User balance state
    const [userBalances, setUserBalances] = useState({ flex: 0, vacation: 0, advanceVacation: 0, advanceVacationUsed: 0 });

    // Unified add dialog state — single-day (tabbed dialog, Task 1.R.1-R)
    const [unifiedAddOpen, setUnifiedAddOpen] = useState(false);
    const [unifiedAddDay, setUnifiedAddDay] = useState(null);
    const [unifiedAddInitialTab, setUnifiedAddInitialTab] = useState('time');
    const [dialogDirty, setDialogDirty] = useState(false);

    // Time entries state for the tabbed dialog's Time tab
    const [selectedDay, setSelectedDay] = useState(null);
    const [dayEntries, setDayEntries] = useState([]);

    // Unified add dialog state — bulk mode (Task 1.7 / reworked 1.R2 Round 2 to
    // reuse the same tabbed dialog as single-day, instead of a separate flow).
    const [unifiedBulkOpen, setUnifiedBulkOpen] = useState(false);

    // Remember last project — declared before `emptyBulkTimeEntry` below, which
    // reads it as a useState lazy initializer on the very first render (a later
    // `const` would still be in the temporal dead zone at that point).
    const [lastProjectId, setLastProjectId] = useState(() => {
        return localStorage.getItem('lastSelectedProject') || '';
    });

    // Bulk mode's Time tab holds exactly one shared entry (fanned out to every
    // selected date on save) — separate from `dayEntries`, which is the
    // single-day dialog's per-day entry list.
    const emptyBulkTimeEntry = () => [{
        tempId: Date.now(),
        project_id: lastProjectId || '',
        activity: '', activity_id: '', hours: '',
        deviation_type_id: '', overtime_type: '', notes: '',
    }];
    const [bulkTimeEntry, setBulkTimeEntry] = useState(emptyBulkTimeEntry);

    // Quick-capture: flag set on mount when a file was passed via the store.
    // Consumed after monthInfo loads so openUnifiedAdd has a valid day object.
    const [pendingQuickCapture, setPendingQuickCapture] = useState(() => {
        // Check synchronously at init — file is already in the store by mount time
        return getQuickCaptureFile() !== null;
    });

    // Holds the File object captured via the quick-capture shortcut until the
    // dialog closes, at which point it is reset to null.
    const [quickCaptureFile, setQuickCaptureFile] = useState(null);

    // Calendar lock state (Phase 9)
    const [weekLocks, setWeekLocks] = useState({});     // { "17": true, "18": false, ... }
    const [monthLocked, setMonthLocked] = useState(false);
    const [lockingWeek, setLockingWeek] = useState(null);   // weekNum being toggled
    const [lockingMonth, setLockingMonth] = useState(false);

    // Stale-response guard: each fetchData call claims a seq number; setters only
    // fire if this call is still the latest one in flight (prevents an older
    // response from overwriting a newer one when the user saves multiple days
    // back-to-back via the next-day chevron).
    const fetchSeqRef = useRef(0);

    useEffect(() => {
        fetchData();
        fetchUserBalances();
    }, [month, year]);

    // Admin-maintained Skatteverket international per-diem link (changes yearly).
    // Loaded once; falls back to the API default if the request fails.
    const [intlRateUrl, setIntlRateUrl] = useState('');
    useEffect(() => {
        let active = true;
        getExpenseSettings()
            .then(res => { if (active) setIntlRateUrl(res.data?.allowance_intl_rate_url || ''); })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    // Consume a pending quick-capture once monthInfo is ready.
    // Retrieves the file from the store, saves it into quickCaptureFile state so
    // it can be passed as initialReceiptFile to UnifiedAddDialog, then opens the
    // dialog on today's date on the Receipt tab.
    // clearQuickCaptureFile() prevents double-trigger on re-renders.
    useEffect(() => {
        if (!pendingQuickCapture || !monthInfo) return;

        const file = getQuickCaptureFile();
        if (!file) {
            setPendingQuickCapture(false);
            return;
        }

        clearQuickCaptureFile();
        setPendingQuickCapture(false);
        setQuickCaptureFile(file);

        const todayStr = new Date().toISOString().split('T')[0];
        const todayObj = monthInfo.days?.find(d => d.date === todayStr) || {
            date: todayStr,
            is_weekend: false,
            is_red_day: false,
            expected_hours: 8,
        };

        openUnifiedAdd(todayObj, 'receipt');
    // openUnifiedAdd reads from component state and is stable per render; eslint
    // would flag it as a missing dep but including it would cause infinite loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingQuickCapture, monthInfo]);

    const fetchUserBalances = async () => {
        try {
            const [flexRes, vacationRes] = await Promise.all([
                getFlexBalance(),
                getVacationBalance()
            ]);
            setUserBalances({
                flex: flexRes.data.balance || 0,
                vacation: vacationRes.data.vacation_days || 0,
                advanceVacation: vacationRes.data.advance_vacation_days || 0,
                advanceVacationUsed: vacationRes.data.advance_vacation_used || 0
            });
        } catch (error) {
            console.error('Failed to fetch balances:', error);
        }
    };

    const handleToggleWeekLock = async (weekNum) => {
        if (lockingWeek || isBothReportsLocked) return;
        setLockingWeek(weekNum);
        const isCurrentlyLocked = weekLocks[String(weekNum)] === true;
        try {
            if (isCurrentlyLocked) {
                setWeekLocks(prev => ({ ...prev, [String(weekNum)]: false }));
                setMonthLocked(false);
                await unlockCalendarWeek(year, month, weekNum);
            } else {
                setWeekLocks(prev => ({ ...prev, [String(weekNum)]: true }));
                await lockCalendarWeek(year, month, weekNum);
            }
        } catch (err) {
            // Revert optimistic update
            setWeekLocks(prev => ({ ...prev, [String(weekNum)]: isCurrentlyLocked }));
            if (!isCurrentlyLocked) setMonthLocked(false);
            toast.error(err.response?.data?.detail || t('common.error'));
        } finally {
            setLockingWeek(null);
        }
    };

    const handleToggleMonthLock = async () => {
        if (lockingMonth || isBothReportsLocked) return;
        setLockingMonth(true);
        const isCurrentlyLocked = monthLocked;
        try {
            if (isCurrentlyLocked) {
                setMonthLocked(false);
                await unlockCalendarMonth(year, month);
            } else {
                setMonthLocked(true);
                await lockCalendarMonth(year, month);
            }
        } catch (err) {
            setMonthLocked(isCurrentlyLocked);
            toast.error(err.response?.data?.detail || t('common.error'));
        } finally {
            setLockingMonth(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        const seq = ++fetchSeqRef.current;
        try {
            const [monthRes, summaryRes, projectsRes, deviationsRes, statusRes, reopenRes, expenseReportRes, expenseReopenRes] = await Promise.all([
                getMonthInfo(month, year),
                getMonthSummary(year, month),
                getProjects(),
                getDeviationTypes(),
                getTimesheetStatus(month, year),
                getReopenRequests(),
                getExpenseReport(month, year).catch(() => null),
                getExpenseReopenRequests().catch(() => ({ data: [] })),
            ]);

            // Discard stale responses: if a newer fetchData call has already
            // started, do not let this one overwrite state with older data.
            if (seq !== fetchSeqRef.current) return;

            setMonthInfo(monthRes.data);
            setEntries(summaryRes.data.time_reports);
            setExpenses(summaryRes.data.expenses);
            setWeekLocks(summaryRes.data.week_locks || {});
            setMonthLocked(summaryRes.data.month_locked || false);
            setProjects(projectsRes.data.filter(p => p.active));
            setDeviationTypes(deviationsRes.data.filter(d => d.active));
            setTimesheetStatus(statusRes.data);
            if (!notesDirtyRef.current && !notesSavingRef.current) {
                setNotesText(statusRes.data?.user_notes || '');
                setNotesDirty(false);
                setNotesSaved(false);
            }
            setExpenseReportStatus(expenseReportRes?.data ?? null);

            const pending = reopenRes.data.find(r =>
                r.month === month && r.year === year && r.status === 'pending'
            );
            setPendingReopenRequest(pending || null);

            const expenseReport = expenseReportRes?.data ?? null;
            const pendingExpenseReopen = expenseReport?.id
                ? expenseReopenRes.data.find(r => r.expense_report_id === expenseReport.id) || null
                : null;
            setPendingExpenseReopenRequest(pendingExpenseReopen);
        } catch (error) {
            if (seq === fetchSeqRef.current) toast.error(t('timesheet.load_error'));
        } finally {
            // Only the latest in-flight request clears loading — a stale older
            // response must not hide the spinner while a newer fetch is running.
            if (seq === fetchSeqRef.current) setLoading(false);
        }
    };

    const handleRequestReopen = async () => {
        if (!reopenReason.trim()) {
            toast.error(t('timesheet.reopen.no_reason_error'));
            return;
        }

        setRequestingReopen(true);
        try {
            await requestTimesheetReopen({
                timesheet_id: timesheetStatus.id,
                reason: reopenReason
            });
            toast.success(t('timesheet.reopen.success_toast'));
            setReopenDialogOpen(false);
            setReopenReason('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('timesheet.reopen.submit_error'));
        } finally {
            setRequestingReopen(false);
        }
    };

    const handleRequestExpenseReopen = async () => {
        if (!expenseReopenReason.trim()) {
            toast.error(t('timesheet.reopen.no_reason_error'));
            return;
        }
        setRequestingExpenseReopen(true);
        try {
            await requestExpenseReopen({
                expense_report_id: expenseReportStatus.id,
                reason: expenseReopenReason
            });
            toast.success(t('timesheet.reopen.success_toast'));
            setExpenseReopenDialogOpen(false);
            setExpenseReopenReason('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('timesheet.reopen.submit_error'));
        } finally {
            setRequestingExpenseReopen(false);
        }
    };

    const handleSaveNotes = async () => {
        if (notesSaving || !notesDirty) return;
        const targetMonth = month;
        const targetYear = year;
        const textToSave = notesText;
        setNotesSaving(true);
        try {
            await saveTimesheetNotes(targetMonth, targetYear, textToSave);
            if (notesTextRef.current === textToSave) {
                setNotesDirty(false);
            }
            setNotesSaved(true);
            setTimesheetStatus(prev => {
                if (!prev) return prev;
                if (currentMonthRef.current === targetMonth && currentYearRef.current === targetYear) {
                    return { ...prev, user_notes: textToSave };
                }
                return prev;
            });
            setTimeout(() => setNotesSaved(false), 3000);
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : t('common.error'));
        } finally {
            setNotesSaving(false);
        }
    };

    const navigateMonth = async (direction) => {
        if (notesSaving) return;
        if (notesDirty) {
            const targetMonth = month;
            const targetYear = year;
            const textToSave = notesText;
            setNotesSaving(true);
            try {
                await saveTimesheetNotes(targetMonth, targetYear, textToSave);
                if (notesTextRef.current !== textToSave) {
                    return;
                }
                setNotesDirty(false);
            } catch (error) {
                const detail = error.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : t('common.error'));
                return;
            } finally {
                setNotesSaving(false);
            }
        }
        if (direction === 'prev') {
            if (month === 1) { setMonth(12); setYear(year - 1); }
            else setMonth(month - 1);
        } else {
            if (month === 12) { setMonth(1); setYear(year + 1); }
            else setMonth(month + 1);
        }
    };

    // Touch-swipe month navigation (Task 6.9)
    const touchStartX = useRef(null);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        if (bulkSelectMode) { touchStartX.current = null; return; }
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(delta) < 50) return;
        if (delta < 0) {
            navigateMonth('next');
        } else {
            navigateMonth('prev');
        }
    };

    const isTimesheetLocked = timesheetStatus?.status === 'submitted' || timesheetStatus?.status === 'approved';
    const isExpenseLocked = expenseReportStatus?.status === 'submitted' || expenseReportStatus?.status === 'approved';
    const isBothReportsLocked = isTimesheetLocked && isExpenseLocked;

    // Single entry point for opening the tabbed unified dialog for a day.
    // Lock state is no longer an early-exit here — the dialog handles per-tab
    // read-only rendering when isTimesheetLocked / isExpenseLocked are passed.
    const openUnifiedAdd = (day, initialTab = 'time') => {
        setUnifiedAddDay(day);
        setSelectedDay(day);
        setUnifiedAddInitialTab(initialTab);

        // Pre-populate dayEntries for the Time tab
        const dayEntriesData = entries.filter(e => e.date === day.date);
        if (dayEntriesData.length === 0) {
            setDayEntries([{
                tempId: Date.now(),
                project_id: lastProjectId || '',
                activity: '',
                hours: '',
                deviation_type_id: '',
                overtime_type: '',
                notes: '',
                miles_km: ''
            }]);
        } else {
            setDayEntries(dayEntriesData.map(e => ({
                ...e,
                tempId: e.id || Date.now(),
                project_id: e.project_id || '',
                deviation_type_id: e.deviation_type_id || '',
                overtime_type: e.overtime_type || '',
                miles_km: e.miles_km || ''
            })));
        }

        setUnifiedAddOpen(true);
    };

    // Navigate the single-day dialog forward or backward one day (Task 1.R.6)
    const handleNavigateDay = (delta) => {
        if (!unifiedAddDay || !monthInfo?.days) return;
        const calendarDays = monthInfo.days;
        const currentIndex = calendarDays.findIndex(d => d.date === unifiedAddDay.date);
        if (currentIndex === -1) return;
        const newIndex = currentIndex + delta;
        if (newIndex < 0 || newIndex >= calendarDays.length) return;
        const newDay = calendarDays[newIndex];
        if (dialogDirty) {
            const confirmed = window.confirm(t('timesheet.unsaved_changes_confirm'));
            if (!confirmed) return;
        }
        // Replicate the state-setting logic from openUnifiedAdd for the new day
        setUnifiedAddDay(newDay);
        setSelectedDay(newDay);
        const dayEntriesData = entries.filter(e => e.date === newDay.date);
        if (dayEntriesData.length === 0) {
            setDayEntries([{
                tempId: Date.now(),
                project_id: lastProjectId || '',
                activity: '',
                hours: '',
                deviation_type_id: '',
                overtime_type: '',
                notes: '',
                miles_km: ''
            }]);
        } else {
            setDayEntries(dayEntriesData.map(e => ({
                ...e,
                tempId: e.id || Date.now(),
                project_id: e.project_id || '',
                deviation_type_id: e.deviation_type_id || '',
                overtime_type: e.overtime_type || '',
                miles_km: e.miles_km || ''
            })));
        }
    };

    // ── Bulk selection helpers ─────────────────────────────────────────────────
    const toggleDaySelection = (dateStr) => {
        if (selectedDays.includes(dateStr)) {
            setSelectedDays(selectedDays.filter(d => d !== dateStr));
        } else {
            setSelectedDays([...selectedDays, dateStr]);
        }
    };

    const isRedDay = (dateStr) => {
        const dayObj = monthInfo?.days?.find(d => d.date === dateStr);
        if (dayObj) return !!dayObj.is_red_day;
        const d = new Date(dateStr + 'T00:00:00');
        return d.getDay() === 0;
    };

    const selectAllWorkingDays = () => {
        if (!monthInfo?.days) return;
        const workingDays = [];
        monthInfo.days.forEach(day => {
            if (day.date && !day.is_weekend && !day.is_red_day) {
                const hasEntries = entries.some(e => e.date === day.date);
                if (!hasEntries) workingDays.push(day.date);
            }
        });
        setSelectedDays(workingDays);
    };

    const clearSelection = () => {
        setSelectedDays([]);
        setBulkSelectMode(false);
    };


    // Submit the time timesheet for admin approval. Independent of the expense
    // report — backend has two collections (timesheets + expense_reports),
    // admin reviews them separately. The Phase 5 5.54 refactor will unify both.
    const handleSubmitTimesheet = async () => {
        if (entries.length === 0) {
            toast.error(t('timesheet.submit.no_entries_error'));
            return;
        }
        setSubmitting(true);
        try {
            await submitTimesheet(month, year);
            toast.success(t('timesheet.submit.success_toast'));
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('timesheet.submit.error'));
        } finally {
            setSubmitting(false);
        }
    };

    // Submit the expense report for admin approval — separate from the timesheet
    // submission. Reviewed by admin via its own /admin/approvals expense queue.
    const handleSubmitExpenseReport = async () => {
        if (expenses.length === 0) {
            toast.error(t('timesheet.expenses.no_entries_error'));
            return;
        }
        setSubmitting(true);
        try {
            await submitExpenseReport(month, year);
            toast.success(t('timesheet.expenses.submit_success'));
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('timesheet.expenses.submit_error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleBulkDelete = async () => {
        const categoryLabels = {
            time: 'time entries',
            mileage: 'mileage expenses',
            receipt: 'receipts',
            allowance: 'allowances',
        };
        const chosenKeys = Object.keys(bulkDeleteCategories).filter(k => bulkDeleteCategories[k]);
        const summary = chosenKeys.map(k => categoryLabels[k]).join(', ');

        const confirmed = window.confirm(
            t('timesheet.bulk_delete.confirm_dialog', { count: selectedDays.length })
        );
        if (!confirmed) {
            setBulkDeleteOpen(false);
            setBulkDeleteCategories({ time: false, mileage: false, receipt: false, allowance: false });
            return;
        }

        setBulkDeleting(true);
        const promises = [];

        if (bulkDeleteCategories.time) {
            promises.push(
                bulkDeleteTimeEntries(selectedDays).then(res => ({ type: 'time', deleted: res.data.deleted }))
            );
        }

        const expenseTypes = [];
        if (bulkDeleteCategories.mileage) expenseTypes.push('mileage');
        if (bulkDeleteCategories.receipt) expenseTypes.push('receipt');
        if (bulkDeleteCategories.allowance) expenseTypes.push('allowance');
        if (expenseTypes.length > 0) {
            promises.push(
                bulkDeleteExpenses(selectedDays, expenseTypes).then(res => ({ type: 'expenses', deleted: res.data.deleted }))
            );
        }

        const results = await Promise.allSettled(promises);
        const failures = results.filter(r => r.status === 'rejected');
        const totalDeleted = results
            .filter(r => r.status === 'fulfilled')
            .reduce((sum, r) => sum + (r.value.deleted || 0), 0);

        if (failures.length === 0) {
            toast.success(t('timesheet.bulk_delete.success_toast', { entryCount: totalDeleted, y: totalDeleted === 1 ? 'y' : 'ies', dateCount: selectedDays.length }));
            fetchData();
            setBulkDeleteOpen(false);
            setBulkSelectMode(false);
            setSelectedDays([]);
            setBulkDeleteCategories({ time: false, mileage: false, receipt: false, allowance: false });
        } else {
            toast.error(t('timesheet.bulk_delete.partial_error'));
            setBulkDeleting(false);
        }
    };

    const getDayTotal = (dateStr) =>
        entries.filter(e => e.date === dateStr).reduce((sum, e) => sum + e.hours, 0);

    const getDayMiles = (dateStr) =>
        entries.filter(e => e.date === dateStr).reduce((sum, e) => sum + (e.miles_km || 0), 0);

    const getWeeklyData = () => {
        if (!monthInfo) return [];
        const weeks = [];
        const firstDayDate = new Date(year, month - 1, 1);
        const firstDayOfWeek = firstDayDate.getDay() === 0 ? 6 : firstDayDate.getDay() - 1;
        const paddedDays = [];
        for (let i = 0; i < firstDayOfWeek; i++) paddedDays.push(null);
        monthInfo.days.forEach(day => paddedDays.push(day));
        while (paddedDays.length % 7 !== 0) paddedDays.push(null);
        for (let i = 0; i < paddedDays.length; i += 7) {
            const weekDays = paddedDays.slice(i, i + 7);
            const firstValidDay = weekDays.find(d => d !== null);
            const weekNum = firstValidDay ? getWeekNumber(new Date(firstValidDay.date)) : null;
            weeks.push({ weekNum, days: weekDays });
        }
        return weeks;
    };

    const expensesByDate = useMemo(() => {
        const map = new Map();
        for (const e of expenses) {
            const bucket = map.get(e.date) || { mileage: [], receipt: [], allowance: [], totalSEK: 0 };
            if (e.expense_category === 'mileage') bucket.mileage.push(e);
            else if (e.expense_category === 'receipt') bucket.receipt.push(e);
            else if (e.expense_category === 'allowance') bucket.allowance.push(e);
            // For receipts, amount_sek is the SEK-converted value (Phase 10).
            // Mileage/allowance still use calculated_amount.
            // ?? amount fallback exists only for legacy un-backfilled receipts.
            bucket.totalSEK += parseFloat(
                e.calculated_amount ?? e.amount_sek ?? e.amount ?? 0
            ) || 0;
            map.set(e.date, bucket);
        }
        return map;
    }, [expenses]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    const totalLogged = entries.reduce((sum, e) => sum + e.hours, 0);
    const totalMiles = entries.reduce((sum, e) => sum + (e.miles_km || 0), 0);
    const expectedTotal = monthInfo?.expected_hours || 0;
    const weeklyData = getWeeklyData();

    // Projected flex delta for the displayed month — mirrors the auto-flex
    // approval-time logic in backend/routes/timesheets.py so the user sees
    // the *net* impact (deposit − withdraw) on still-draft entries.
    // Withdrawals: hours on entries whose deviation name starts with "flex".
    // Deposits (auto): OT-paid hours (billable project + OT type) are excluded;
    // remaining extra hours on flex-eligible project entries are banked.
    // Unclamped against the [-40, 80] balance limits — the absolute balance
    // lives on the profile card.
    const flexImpact = (() => {
        const byDate = new Map();
        for (const e of entries) {
            if (!byDate.has(e.date)) byDate.set(e.date, []);
            byDate.get(e.date).push(e);
        }
        const dayBaseByDate = new Map();
        if (monthInfo?.days) {
            for (const d of monthInfo.days) {
                dayBaseByDate.set(d.date, d.expected_hours ?? 8);
            }
        }
        const billableIds = new Set(projects.filter(p => p.billable).map(p => p.id));
        const OT_PAID = new Set(["ot1", "ot2", "ot_red", "standard", "special"]);

        let withdraw = 0;
        let deposit = 0;
        const depositByDate = new Map(); // date → hours that will auto-flex
        for (const [date, dayEntries] of byDate.entries()) {
            const dayBase = dayBaseByDate.get(date) ?? 8;
            let dayTotal = 0;
            let flexWithdraw = 0;
            let otPaidHours = 0;
            let flexEligibleHours = 0;

            for (const e of dayEntries) {
                const hrs = Number(e.hours || 0);
                dayTotal += hrs;
                const name = (e.deviation_name || '').toLowerCase();
                if (e.deviation_type_id && name.startsWith('flex')) {
                    flexWithdraw += hrs;
                } else if (e.project_id && !e.deviation_type_id) {
                    const isBillable = billableIds.has(e.project_id);
                    const isOtPaid = isBillable && OT_PAID.has(e.overtime_type);
                    if (isOtPaid) {
                        otPaidHours += hrs;
                    } else {
                        flexEligibleHours += hrs;
                    }
                }
            }

            withdraw += flexWithdraw;

            const totalForExtra = dayTotal - flexWithdraw;
            const extra = Math.max(0, totalForExtra - dayBase);
            if (extra <= 0) continue;

            const extraAfterOt = Math.max(0, extra - otPaidHours);
            const dayDeposit = Math.min(extraAfterOt, flexEligibleHours);
            deposit += dayDeposit;
            if (dayDeposit > 0) depositByDate.set(date, dayDeposit);
        }
        return { deposit, withdraw, net: deposit - withdraw, depositByDate };
    })();
    const flexDepositByDate = flexImpact.depositByDate;
    const formatFlexHours = (h) => {
        if (h === 0) return '0h';
        const sign = h > 0 ? '+' : '−';
        const absH = Math.abs(h);
        const num = Number.isInteger(absH) ? absH.toFixed(0) : absH.toFixed(1);
        return `${sign}${num}h`;
    };
    // Monthly expense totals — mileage + allowances are SEK (calculated_amount);
    // receipts may be in other currencies but the month-summary `amount` field
    // is already the displayable one. We sum `amount` by category for the header
    // summary; mixed currencies are rare enough to defer (Phase 5 if needed).
    const expenseByCategory = expenses.reduce((acc, e) => {
        let amt = 0;
        if (e.expense_category === 'receipt') {
            amt = Number(e.amount_sek ?? e.amount) || 0;
            acc.receipts += amt;
        } else if (e.expense_category === 'mileage') {
            amt = Number(e.calculated_amount ?? e.amount) || 0;
            acc.mileage += amt;
        } else if (e.expense_category === 'allowance') {
            amt = Number(e.calculated_amount ?? e.amount) || 0;
            acc.allowances += amt;
        }
        acc.count += 1;
        return acc;
    }, { mileage: 0, receipts: 0, allowances: 0, count: 0 });
    const expenseGrandTotal = expenseByCategory.mileage + expenseByCategory.receipts + expenseByCategory.allowances;

    return (
        <div className="space-y-6" data-testid="timesheet-page">
            {/* Header: Title on left, Month navigation in middle, Stat cards on right */}
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                <div className="shrink-0">
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">{t('timesheet.page_title')}</h1>
                    <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">{t('timesheet.page_subtitle')}</p>
                </div>

                {/* Month navigation — fitted between page header and stat cards (Task 22.6) */}
                <div className="flex justify-center items-center flex-1 max-w-3xl xl:max-w-4xl w-full mx-auto xl:mx-6">
                    <MonthNavigator
                        year={year}
                        month={month}
                        onPrev={() => navigateMonth('prev')}
                        onNext={() => navigateMonth('next')}
                        className="w-full border border-border bg-background py-1.5 px-4 sm:px-6"
                        adornment={(
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded transition-colors
                                                ${monthLocked
                                                    ? 'text-amber-600 dark:text-amber-400 hover:text-amber-700'
                                                    : 'text-muted-foreground hover:text-foreground'}`}
                                            aria-label={monthLocked ? t('timesheet.month_lock.unlock_aria') : t('timesheet.month_lock.lock_aria')}
                                            onClick={handleToggleMonthLock}
                                            disabled={lockingMonth || isBothReportsLocked || (!monthLocked && !(Object.keys(weekLocks).length > 0 && Object.values(weekLocks).every(v => v === true)))}
                                            data-testid="month-lock-toggle"
                                        >
                                            {monthLocked ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {isBothReportsLocked
                                            ? t('timesheet.lock_frozen_tooltip')
                                            : monthLocked
                                                ? t('timesheet.month_lock.unlock_tooltip')
                                                : Object.keys(weekLocks).length > 0 && Object.values(weekLocks).every(v => v === true)
                                                    ? t('timesheet.month_lock.lock_tooltip')
                                                    : t('timesheet.month_lock.lock_weeks_first')}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    />
                </div>

                {/* Right-aligned metrics row. Order (left → right):
                    [Expenses] [Miles] [Flex Bank] [Current Flex] [Hours]  — Hours is the primary /
                    anchor; new metrics extend to its left (inner side). Each
                    metric is a plain text-only column, no decorative category
                    icons, so visual weight stays consistent with Hours. */}
                <div className="flex gap-2 flex-wrap sm:justify-end shrink-0">
                    {expenseGrandTotal > 0 && (
                        <div className="border border-border bg-background px-3 py-2 text-center min-w-[72px]">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-widest leading-tight">{t('timesheet.header.expenses_label')}</p>
                            <p className="font-mono text-base sm:text-xl font-bold mt-0.5">{formatSEK(expenseGrandTotal)}</p>
                        </div>
                    )}
                    {totalMiles > 0 && (
                        <div className="border border-border bg-background px-3 py-2 text-center min-w-[72px]">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-widest leading-tight">{t('timesheet.header.miles_label')}</p>
                            <p className="font-mono text-base sm:text-xl font-bold mt-0.5">{totalMiles}</p>
                        </div>
                    )}
                    {/* Flex Bank — accumulated approved balance (always visible) */}
                    <div
                        className="border border-border bg-background px-3 py-2 text-center min-w-[72px]"
                        title={t('timesheet.header.flex_bank_tooltip')}
                        data-testid="timesheet-flex-bank-card"
                    >
                        <p className="text-[10px] text-muted-foreground uppercase tracking-widest leading-tight">{t('timesheet.header.flex_bank_label')}</p>
                        <p className={`font-mono text-base sm:text-xl font-bold mt-0.5 ${userBalances.flex > 0 ? 'text-green-700 dark:text-green-400' : userBalances.flex < 0 ? 'text-amber-700 dark:text-amber-400' : ''}`}>
                            {formatFlexHours(userBalances.flex)}
                        </p>
                    </div>
                    {/* Current Flex — projected net for this month (always visible).
                        Dimmed when timesheet is approved because the impact is
                        already reflected in the Flex Bank balance above. */}
                    <div
                        className={`border border-border bg-background px-3 py-2 text-center min-w-[72px] ${timesheetStatus?.status === 'approved' ? 'opacity-50' : ''}`}
                        title={timesheetStatus?.status === 'approved'
                            ? t('timesheet.header.flex_approved_tooltip')
                            : t('timesheet.header.flex_breakdown', {
                                deposit: flexImpact.deposit % 1 === 0 ? flexImpact.deposit.toFixed(0) : flexImpact.deposit.toFixed(1),
                                withdraw: flexImpact.withdraw % 1 === 0 ? flexImpact.withdraw.toFixed(0) : flexImpact.withdraw.toFixed(1),
                            })}
                        data-testid="timesheet-flex-card"
                    >
                        <p className="text-[10px] text-muted-foreground uppercase tracking-widest leading-tight">{t('timesheet.header.flex_label')}</p>
                        <p className={`font-mono text-base sm:text-xl font-bold mt-0.5 ${
                            timesheetStatus?.status === 'approved'
                                ? 'text-muted-foreground'
                                : flexImpact.net > 0 ? 'text-green-700 dark:text-green-400'
                                : flexImpact.net < 0 ? 'text-amber-700 dark:text-amber-400'
                                : ''
                        }`}>
                            {formatFlexHours(flexImpact.net)}
                        </p>
                    </div>
                    <div className="border border-border bg-background px-3 py-2 text-center min-w-[72px]">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-widest leading-tight">{t('timesheet.header.hours_label')}</p>
                        <p className="font-mono text-base sm:text-xl font-bold mt-0.5">{totalLogged} / {expectedTotal}h</p>
                    </div>
                </div>
            </div>

            {/* Dual Status Banner — Timesheet row + Expenses row.
                Each row only renders when the respective report has a
                noteworthy status (submitted / approved / rejected).
                Draft / null reports are silent — no row shown. */}
            {(() => {
                const tsStatus = timesheetStatus?.status;
                const expStatus = expenseReportStatus?.status;
                const showTs = tsStatus === 'submitted' || tsStatus === 'approved' || tsStatus === 'rejected';
                const showExp = expStatus === 'submitted' || expStatus === 'approved' || expStatus === 'rejected';
                if (!showTs && !showExp) return null;

                // ── Per-row style helpers ────────────────────────────────────
                const rowBg = (status) => {
                    if (status === 'approved') return 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900';
                    if (status === 'rejected') return 'bg-red-50 border-destructive dark:bg-red-950/30';
                    return 'bg-muted border-border';
                };
                const iconColor = (status) => {
                    if (status === 'approved') return 'text-green-600 dark:text-green-400';
                    if (status === 'rejected') return 'text-destructive';
                    return '';
                };
                const textColor = (status) => {
                    if (status === 'approved') return 'text-green-800 dark:text-green-200';
                    if (status === 'rejected') return 'text-destructive';
                    return '';
                };
                const descColor = (status) => {
                    if (status === 'approved') return 'text-green-600 dark:text-green-400';
                    if (status === 'rejected') return 'text-red-600 dark:text-red-400';
                    return 'text-muted-foreground';
                };

                // ── Status description text ──────────────────────────────────
                const tsDesc = () => {
                    if (tsStatus === 'submitted') return t('timesheet.status.submitted_desc', { date: formatDate(timesheetStatus.submitted_at) });
                    if (tsStatus === 'approved') return t('timesheet.status.approved_desc', { date: formatDate(timesheetStatus.reviewed_at) });
                    return null;
                };
                const expDesc = () => {
                    if (expStatus === 'submitted') return t('timesheet.status.expense_submitted_desc', { date: formatDate(expenseReportStatus?.submitted_at) });
                    if (expStatus === 'approved') return t('timesheet.status.expense_approved_desc', { date: formatDate(expenseReportStatus?.reviewed_at) });
                    return null;
                };

                return (
                    <div className="border border-border flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-border">
                        {/* ── Timesheet row ─────────────────────────────────── */}
                        {showTs && (
                            <div className={`flex items-center gap-3 p-4 sm:flex-1 sm:basis-0 sm:min-w-0 ${rowBg(tsStatus)}`}>
                                {tsStatus === 'rejected'
                                    ? <AlertCircle className={`w-5 h-5 flex-shrink-0 ${iconColor(tsStatus)}`} />
                                    : <Lock className={`w-5 h-5 flex-shrink-0 ${iconColor(tsStatus)}`} />
                                }
                                <div className="flex-1 min-w-0">
                                    <p className={`font-medium text-sm ${textColor(tsStatus)}`}>
                                        <span className="font-semibold">{t('timesheet.status.timesheet_label')}</span>
                                        {' — '}
                                        {tsStatus === 'submitted' && t('timesheet.status.submitted')}
                                        {tsStatus === 'approved' && t('timesheet.status.approved')}
                                        {tsStatus === 'rejected' && t('timesheet.status.rejected')}
                                    </p>
                                    {tsDesc() && (
                                        <p className={`text-xs mt-0.5 ${descColor(tsStatus)}`}>{tsDesc()}</p>
                                    )}
                                    {tsStatus === 'rejected' && timesheetStatus.rejection_comment && (
                                        <p className={`text-xs mt-0.5 ${descColor(tsStatus)}`}>
                                            {t('timesheet.status.rejection_comment', { comment: timesheetStatus.rejection_comment })}
                                        </p>
                                    )}
                                    {(tsStatus === 'submitted' || tsStatus === 'approved') && pendingReopenRequest && (
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{t('timesheet.status.reopen_pending')}</p>
                                    )}
                                </div>
                                {(tsStatus === 'submitted' || tsStatus === 'approved') && !pendingReopenRequest && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setReopenDialogOpen(true)}
                                        className={`uppercase tracking-wider text-xs min-h-[44px] flex-shrink-0 ${tsStatus === 'approved' ? 'border-green-300 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-950' : ''}`}
                                        data-testid="request-reopen-btn"
                                    >
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                        {t('timesheet.status.reopen_button')}
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* ── Expenses row ──────────────────────────────────── */}
                        {showExp && (
                            <div className={`flex items-center gap-3 p-4 sm:flex-1 sm:basis-0 sm:min-w-0 ${rowBg(expStatus)}`}>
                                {expStatus === 'rejected'
                                    ? <AlertCircle className={`w-5 h-5 flex-shrink-0 ${iconColor(expStatus)}`} />
                                    : <Lock className={`w-5 h-5 flex-shrink-0 ${iconColor(expStatus)}`} />
                                }
                                <div className="flex-1 min-w-0">
                                    <p className={`font-medium text-sm ${textColor(expStatus)}`}>
                                        <span className="font-semibold">{t('timesheet.status.expenses_label')}</span>
                                        {' — '}
                                        {expStatus === 'submitted' && t('timesheet.status.expense_submitted')}
                                        {expStatus === 'approved' && t('timesheet.status.expense_approved')}
                                        {expStatus === 'rejected' && t('timesheet.status.expense_rejected')}
                                    </p>
                                    {expDesc() && (
                                        <p className={`text-xs mt-0.5 ${descColor(expStatus)}`}>{expDesc()}</p>
                                    )}
                                    {expStatus === 'rejected' && (expenseReportStatus?.review_comment || expenseReportStatus?.rejection_comment) && (
                                        <p className={`text-xs mt-0.5 ${descColor(expStatus)}`}>
                                            {t('timesheet.status.rejection_comment', { comment: expenseReportStatus.review_comment || expenseReportStatus.rejection_comment })}
                                        </p>
                                    )}
                                    {(expStatus === 'submitted' || expStatus === 'approved') && pendingExpenseReopenRequest && (
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{t('timesheet.status.reopen_pending')}</p>
                                    )}
                                </div>
                                {(expStatus === 'submitted' || expStatus === 'approved') && !pendingExpenseReopenRequest && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setExpenseReopenDialogOpen(true)}
                                        className={`uppercase tracking-wider text-xs min-h-[44px] flex-shrink-0 ${expStatus === 'approved' ? 'border-green-300 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-950' : ''}`}
                                        data-testid="request-expense-reopen-btn"
                                    >
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                        {t('timesheet.status.reopen_button')}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                );
            })()}



            {/* Calendar Grid with Week Numbers */}
            <div className="border border-border"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}>
                {/* Day headers */}
                <div className="grid border-b border-border" style={{ gridTemplateColumns: CALENDAR_COLUMNS }}>
                    <div className="p-1 text-center text-[10px] font-bold uppercase border-r border-border bg-muted">{t('timesheet.calendar.week_header')}</div>
                    {['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7'].map((day, idx) => (
                        <div key={day} className={`px-0.5 py-3 sm:p-3 text-center text-xs font-medium uppercase tracking-normal sm:tracking-widest overflow-hidden border-r border-border last:border-r-0 ${idx >= 5 ? 'bg-muted/50' : ''}`}>
                            {day}
                            <span className="block text-[10px] text-muted-foreground font-normal">
                                {getShortDayNames()[idx]}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Weeks */}
                {weeklyData.map((week, weekIdx) => {
                    // sv-SE formats the LOCAL date as YYYY-MM-DD (toISOString would be UTC).
                    const summary = weekSummary(week.days, entries, new Date().toLocaleDateString('sv-SE'));
                    return (
                    <div key={weekIdx} className="grid border-b border-border last:border-b-0" style={{ gridTemplateColumns: CALENDAR_COLUMNS }}>
                        <div className="border-r border-border bg-muted flex flex-col items-center justify-center min-h-[80px] gap-0.5 p-0.5">
                            <span className="font-mono font-bold text-xs">{week.weekNum || ''}</span>
                            {week.weekNum && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className={`min-w-[28px] min-h-[28px] flex items-center justify-center rounded transition-colors
                                                    ${weekLocks[String(week.weekNum)]
                                                        ? 'text-amber-600 dark:text-amber-400 hover:text-amber-700'
                                                        : 'text-muted-foreground hover:text-foreground'}`}
                                                aria-label={weekLocks[String(week.weekNum)]
                                                    ? t('timesheet.week_lock.unlock_aria', { week: week.weekNum })
                                                    : t('timesheet.week_lock.lock_aria', { week: week.weekNum })}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleWeekLock(week.weekNum);
                                                }}
                                                disabled={lockingWeek === week.weekNum || isBothReportsLocked}
                                            >
                                                {weekLocks[String(week.weekNum)]
                                                    ? <Lock className="w-3 h-3" />
                                                    : <Unlock className="w-3 h-3" />}
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                            {isBothReportsLocked
                                                ? t('timesheet.lock_frozen_tooltip')
                                                : weekLocks[String(week.weekNum)]
                                                    ? t('timesheet.week_lock.unlock_tooltip', { week: week.weekNum })
                                                    : t('timesheet.week_lock.lock_tooltip', { week: week.weekNum })}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                            {week.weekNum && (
                                <span
                                    className={`font-mono text-[10px] leading-none whitespace-nowrap rounded border px-1 py-0.5 ${WEEK_SUMMARY_TONE[summary.status]}`}
                                    title={t('timesheet.calendar.week_summary', { logged: formatNumber(summary.logged), norm: formatNumber(summary.norm) })}
                                    data-testid={`week-summary-${week.weekNum}`}
                                >
                                    <span className="font-bold">{formatNumber(summary.logged)}</span>/{formatNumber(summary.norm)}
                                </span>
                            )}
                        </div>

                        {week.days.map((day, dayIdx) => {
                            if (!day) {
                                return (
                                    <div key={`empty-${weekIdx}-${dayIdx}`} className="day-cell bg-muted/30 border-r border-border last:border-r-0 min-h-[80px]"></div>
                                );
                            }

                            const dayEntriesData = entries.filter(e => e.date === day.date);
                            const dayTotal = getDayTotal(day.date);
                            const isToday = day.date === new Date().toISOString().split('T')[0];
                            const isSelected = selectedDays.includes(day.date);
                            // Bulk-select allows any working-day cell regardless of existing entries —
                            // the bulk dialog creates its entry on every selected date without a
                            // pre-existing-entry check (multiple mileage/time entries per day are
                            // already a normal, supported case outside of bulk mode too).
                            const canSelect = bulkSelectMode && !day.is_weekend && !day.is_red_day;

                            return (
                                <div
                                    key={day.date}
                                    style={{ paddingBottom: '2.5rem' }}
                                    className={`day-cell border-r border-border last:border-r-0 min-h-[80px] min-w-0 overflow-hidden relative
                                        ${day.is_weekend ? 'weekend' : ''}
                                        ${day.is_red_day ? 'red-day' : ''}
                                        ${isToday ? 'today' : ''}
                                        ${(isTimesheetLocked && isExpenseLocked) ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:bg-muted/50'}
                                        ${isSelected ? 'ring-2 ring-primary ring-inset bg-primary/10' : ''}
                                    `}
                                    onClick={() => {
                                        if (bulkSelectMode && canSelect) {
                                            toggleDaySelection(day.date);
                                        } else if (!bulkSelectMode) {
                                            openUnifiedAdd(day);
                                        }
                                    }}
                                    data-testid={`day-cell-${day.date}`}
                                >
                                    {/* Bulk selection checkbox — bottom-right, away from top-content */}
                                    {bulkSelectMode && canSelect && (
                                        <div className="absolute bottom-1 right-1 z-10">
                                            <Checkbox checked={isSelected} className="h-4 w-4" />
                                        </div>
                                    )}

                                    <div className="flex flex-col mb-1">
                                        <span className={`font-mono text-sm ${isToday ? 'font-bold' : ''}`}>
                                            {parseInt(day.date.split('-')[2])}
                                        </span>
                                        {day.holiday_name && (
                                            <span className="text-[9px] leading-tight text-red-700 dark:text-red-400 font-medium truncate max-w-full" title={day.holiday_name}>
                                                {day.holiday_name}
                                            </span>
                                        )}
                                        {dayTotal > 0 && (
                                            <span className="inline-flex items-center gap-0.5 text-xs font-mono font-bold mt-0.5">
                                                <Clock className="w-4 h-4 text-slate-600 dark:text-slate-400" aria-label={t('timesheet.icons.time_report')} title={t('timesheet.icons.time_report')} />
                                                {dayTotal}h
                                            </span>
                                        )}
                                    </div>

                                    {/* Auto-flex indicator — shown when this day will deposit flex on approval */}
                                    {flexDepositByDate.has(day.date) && (
                                        <div className="flex items-center gap-0.5 text-green-600 dark:text-green-400 mb-0.5" title={`+${flexDepositByDate.get(day.date).toFixed(1)}h flex`}>
                                            <TrendingUp className="w-3 h-3" />
                                            <span className="text-[9px] font-mono font-medium">+{flexDepositByDate.get(day.date).toFixed(1)}h</span>
                                        </div>
                                    )}

                                    {/* Pills row — info-only, desktop only (hidden on mobile) */}
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="hidden sm:flex flex-col gap-0.5 max-w-full min-w-0 overflow-hidden">
                                                    {dayEntriesData.slice(0, 2).map((entry, i) => {
                                                        let pillClass = 'time-entry-pill truncate text-[9px] min-w-0';
                                                        if (entry.deviation_type_id) pillClass += ' deviation';
                                                        else if (entry.overtime_type === 'flex_save') pillClass += ' flex-deposit';
                                                        else if (entry.overtime_type === 'ot_red' && projects.find(p => p.id === entry.project_id)?.billable) pillClass += ' ot-red';
                                                        else if (entry.overtime_type && entry.overtime_type !== 'flex_save' && projects.find(p => p.id === entry.project_id)?.billable) pillClass += ' overtime';
                                                        else if (entry.project_id && flexDepositByDate.has(day.date)) pillClass += ' flex-deposit';
                                                        const baseName = entry.project_name || entry.deviation_name || '';
                                                        const label = `${baseName} (${entry.hours}h)`;
                                                        const showOverflow = i === 1 && dayEntriesData.length > 2;
                                                        return (
                                                            <div key={entry.id} className="flex items-center gap-1 min-w-0">
                                                                <div className={pillClass}>{label}</div>
                                                                {showOverflow && (
                                                                    <span className="text-[9px] text-muted-foreground whitespace-nowrap shrink-0">
                                                                        +{dayEntriesData.length - 2}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </TooltipTrigger>
                                            {dayEntriesData.length > 0 && (
                                                <TooltipContent side="top" align="start" className="max-w-[360px] p-3">
                                                    <div className="space-y-2">
                                                        <div className="text-xs font-medium">
                                                            {dayEntriesData.length} entr{dayEntriesData.length === 1 ? 'y' : 'ies'}
                                                        </div>
                                                        <div className="space-y-1">
                                                            {dayEntriesData.map(entry => {
                                                                let pillClass = 'time-entry-pill text-[10px] max-w-full';
                                                                if (entry.deviation_type_id) pillClass += ' deviation';
                                                                else if (entry.overtime_type === 'flex_save') pillClass += ' flex-deposit';
                                                                else if (entry.overtime_type === 'ot_red' && projects.find(p => p.id === entry.project_id)?.billable) pillClass += ' ot-red';
                                                        else if (entry.overtime_type && entry.overtime_type !== 'flex_save' && projects.find(p => p.id === entry.project_id)?.billable) pillClass += ' overtime';
                                                                else if (entry.project_id && flexDepositByDate.has(day.date)) pillClass += ' flex-deposit';
                                                                return (
                                                                    <div key={entry.id} className={pillClass} style={{ whiteSpace: 'normal' }}>
                                                                        {entry.project_name || entry.deviation_name} ({entry.hours}h)
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    </TooltipProvider>

                                    {/* Expense presence-indicator icons + SEK total — pinned to bottom-left of
                                        the cell. Each icon is its own tooltip. SEK total trails the icons inline. */}
                                    {(() => {
                                        const flags = expensesByDate.get(day.date) || { mileage: [], receipt: [], allowance: [], totalSEK: 0 };
                                        if (flags.mileage.length === 0 && flags.receipt.length === 0 && flags.allowance.length === 0) return null;

                                        const mileageTotal = flags.mileage.reduce((sum, m) => {
                                            const d = parseFloat(m.distance_mil) || 0;
                                            return sum + (m.round_trip ? d * 2 : d);
                                        }, 0);
                                        const mileageAmount = flags.mileage.reduce((sum, m) => sum + (parseFloat(m.calculated_amount) || 0), 0);
                                        const receiptTotal = flags.receipt.reduce((sum, r) => sum + (parseFloat(r.amount_sek ?? r.amount) || 0), 0);
                                        const receiptCurrency = flags.receipt[0]?.currency || 'SEK';
                                        const allowanceAmount = flags.allowance.reduce((sum, a) => sum + (parseFloat(a.calculated_amount) || 0), 0);

                                        return (
                                            <div className="absolute bottom-1 left-1 right-7 flex items-center gap-2">
                                                {flags.mileage.length > 0 && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Car className="w-5 h-5 text-blue-600 dark:text-blue-400" aria-label={t('timesheet.icons.mileage_expense')} />
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="max-w-[280px] p-3">
                                                                <div className="space-y-1">
                                                                    <div className="text-xs font-medium">
                                                                        {flags.mileage.length} trip{flags.mileage.length === 1 ? '' : 's'} — {mileageTotal.toFixed(1)} mil{mileageAmount > 0 && ` · ${mileageAmount.toFixed(0)} SEK`}
                                                                    </div>
                                                                    <div className="space-y-0.5 text-[11px]">
                                                                        {flags.mileage.map(m => (
                                                                            <div key={m.id}>
                                                                                {m.from_location} → {m.to_location} ({m.distance_mil} mil{m.round_trip ? ' rt' : ''})
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {flags.receipt.length > 0 && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Receipt className="w-5 h-5 text-emerald-600 dark:text-emerald-400" aria-label={t('timesheet.icons.receipt_expense')} />
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="max-w-[280px] p-3">
                                                                <div className="space-y-1">
                                                                    <div className="text-xs font-medium">
                                                                        {flags.receipt.length} receipt{flags.receipt.length === 1 ? '' : 's'} — {receiptTotal.toFixed(0)} {receiptCurrency}
                                                                    </div>
                                                                    <div className="space-y-0.5 text-[11px]">
                                                                        {flags.receipt.map(r => (
                                                                            <div key={r.id}>
                                                                                {RECEIPT_TYPE_LABELS[r.receipt_type] || r.receipt_type || t('expenses.receipt.file_label')}: {parseFloat(r.amount || 0).toFixed(0)} {r.currency || 'SEK'}{r.description ? ` — ${r.description}` : ''}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {flags.allowance.length > 0 && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Hotel className="w-5 h-5 text-amber-600 dark:text-amber-400" aria-label={t('timesheet.icons.allowance')} />
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="max-w-[280px] p-3">
                                                                <div className="space-y-1">
                                                                    <div className="text-xs font-medium">
                                                                        {flags.allowance.length} allowance{flags.allowance.length === 1 ? '' : 's'}{allowanceAmount > 0 && ` — ${allowanceAmount.toFixed(0)} SEK`}
                                                                    </div>
                                                                    <div className="space-y-0.5 text-[11px]">
                                                                        {flags.allowance.map(a => (
                                                                            <div key={a.id}>
                                                                                {a.destination || '—'}{a.nights ? ` · ${a.nights} night${a.nights === 1 ? '' : 's'}` : ''}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {flags.totalSEK > 0 && (
                                                    <span className="hidden sm:inline text-[10px] text-muted-foreground font-mono leading-none ml-auto">
                                                        {formatSEK(flags.totalSEK)}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            );
                        })}
                    </div>
                    );
                })}
            </div>

            {/* Calendar Controls Bar: Bulk Add, Notes, and Submit actions below Calendar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/30 border border-border">
                <div className="flex items-center gap-2 flex-wrap">
                    {!isTimesheetLocked && (
                        <Button
                            variant={bulkSelectMode ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                                setBulkSelectMode(!bulkSelectMode);
                                if (bulkSelectMode) clearSelection();
                            }}
                            className="uppercase tracking-wider text-xs min-h-[40px]"
                            data-testid="bulk-select-mode-btn"
                        >
                            {bulkSelectMode ? <CheckSquare className="w-4 h-4 mr-1.5" /> : <Square className="w-4 h-4 mr-1.5" />}
                            {bulkSelectMode ? t('timesheet.bulk_mode.on') : t('timesheet.bulk_mode.off')}
                        </Button>
                    )}

                    <Button
                        variant={notesOpen ? 'secondary' : 'outline'}
                        size="sm"
                        onClick={() => setNotesOpen(!notesOpen)}
                        className="uppercase tracking-wider text-xs min-h-[40px] relative"
                        data-testid="toggle-notes-btn"
                    >
                        <FileText className="w-4 h-4 mr-1.5" />
                        {t('timesheet.notes.button')}
                        {notesText?.trim() && (
                            <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-primary" title={t('timesheet.notes.has_notes')} />
                        )}
                    </Button>

                    {!isTimesheetLocked && bulkSelectMode && (
                        <>
                            <Button variant="outline" size="sm" onClick={selectAllWorkingDays}
                                className="uppercase tracking-wider text-xs min-h-[40px]" data-testid="select-all-working-btn">
                                {t('timesheet.bulk_mode.select_all')}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={clearSelection}
                                className="uppercase tracking-wider text-xs min-h-[40px]">
                                {t('timesheet.bulk_mode.clear', { count: selectedDays.length })}
                            </Button>
                        </>
                    )}

                    {!isTimesheetLocked && bulkSelectMode && selectedDays.length > 0 && (
                        <div className="flex items-center gap-2">
                            <Button size="sm" onClick={() => { setBulkTimeEntry(emptyBulkTimeEntry()); setUnifiedBulkOpen(true); }}
                                className="uppercase tracking-wider text-xs min-h-[40px]" data-testid="add-to-selected-btn">
                                <Plus className="w-4 h-4 mr-1" />
                                {t('timesheet.bulk_mode.add_button', { count: selectedDays.length })}
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => setBulkDeleteOpen(true)}
                                className="uppercase tracking-wider text-xs min-h-[40px]" data-testid="delete-from-selected-btn">
                                <Trash2 className="w-4 h-4 mr-1" />
                                {t('timesheet.bulk_mode.delete_button', { count: selectedDays.length })}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Right controls: Submit buttons */}
                {((!isTimesheetLocked && entries.length > 0) || (!isExpenseLocked && expenses.length > 0)) && (
                    <div className="flex items-center gap-2 flex-wrap sm:justify-end">
                        {!isTimesheetLocked && entries.length > 0 && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span>
                                            <Button onClick={handleSubmitTimesheet} disabled={submitting || !monthLocked}
                                                size="sm"
                                                className="uppercase tracking-wider min-h-[40px]" data-testid="submit-timesheet-btn">
                                                <Send className="w-4 h-4 mr-2" />
                                                {submitting ? t('common.processing') : t('timesheet.submit_button', { count: entries.length })}
                                            </Button>
                                        </span>
                                    </TooltipTrigger>
                                    {!monthLocked && (
                                        <TooltipContent>{t('timesheet.lock_month_to_submit')}</TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        {!isExpenseLocked && expenses.length > 0 && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span>
                                            <Button onClick={handleSubmitExpenseReport} disabled={submitting || !monthLocked}
                                                variant="outline"
                                                size="sm"
                                                className="uppercase tracking-wider min-h-[40px]" data-testid="submit-expense-report-btn">
                                                <Send className="w-4 h-4 mr-2" />
                                                {submitting ? t('common.processing') : t('timesheet.submit_expenses_button', { count: expenses.length })}
                                            </Button>
                                        </span>
                                    </TooltipTrigger>
                                    {!monthLocked && (
                                        <TooltipContent>{t('timesheet.lock_month_to_submit')}</TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                )}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-6 text-xs uppercase tracking-widest text-muted-foreground">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-muted border border-border"></div>
                    <span>{t('timesheet.legend.weekend')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 red-day border border-border"></div>
                    <span>{t('timesheet.legend.red_day')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-foreground"></div>
                    <span>{t('timesheet.legend.regular_time')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4" style={{backgroundColor: 'hsl(220 90% 56%)'}}></div>
                    <span>{t('timesheet.legend.overtime')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4" style={{backgroundColor: 'hsl(0 72% 50%)'}}></div>
                    <span>{t('timesheet.legend.ot_red')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-foreground" style={{borderLeft: '3px solid hsl(142 76% 36%)'}}></div>
                    <span>{t('timesheet.legend.flex_save')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4" style={{backgroundColor: 'hsl(25 90% 52%)'}}></div>
                    <span>{t('timesheet.legend.deviation')}</span>
                </div>
                {(isTimesheetLocked || isExpenseLocked) && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Lock className="w-4 h-4" />
                        <span>{t('timesheet.legend.locked')}</span>
                    </div>
                )}
            </div>

            {/* User Notes Free Text Area — opens when user clicks Notes button */}
            {notesOpen && (
                <div className="border border-border bg-card p-4 sm:p-5 space-y-3" data-testid="timesheet-notes-section">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-primary" />
                            <h3 className="font-heading text-sm font-semibold tracking-wide uppercase">
                                {t('timesheet.notes.title')}
                            </h3>
                        </div>
                        <div className="flex items-center gap-2">
                            {notesSaving ? (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <RefreshCw className="w-3 h-3 animate-spin" /> {t('timesheet.notes.saving')}
                                </span>
                            ) : notesSaved ? (
                                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 font-medium">
                                    <Check className="w-3.5 h-3.5" /> {t('timesheet.notes.saved')}
                                </span>
                            ) : notesDirty ? (
                                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                    {t('timesheet.notes.unsaved')}
                                </span>
                            ) : null}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setNotesOpen(false)}
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                aria-label={t('timesheet.notes.close')}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {t('timesheet.notes.subtitle')}
                    </p>
                    <Textarea
                        value={notesText}
                        onChange={(e) => {
                            setNotesText(e.target.value);
                            setNotesDirty(true);
                            setNotesSaved(false);
                        }}
                        onBlur={handleSaveNotes}
                        placeholder={t('timesheet.notes.placeholder')}
                        maxLength={5000}
                        rows={4}
                        className="resize-y font-sans text-sm min-h-[96px]"
                        data-testid="timesheet-notes-textarea"
                    />
                    <div className="flex justify-end gap-2">
                        <Button
                            size="sm"
                            onClick={handleSaveNotes}
                            disabled={notesSaving || !notesDirty}
                            className="uppercase tracking-wider text-xs"
                            data-testid="save-notes-btn"
                        >
                            {notesSaving ? t('timesheet.notes.saving') : t('timesheet.notes.save')}
                        </Button>
                    </div>
                </div>
            )}

            {/* Submission history — collapsed by default; lazy-loads past
                timesheets + expense reports on first expand. Two tabs mirror
                the two backend collections (timesheets + expense_reports). */}
            <SubmissionHistorySection />

            {/* Reopen Request Dialog */}
            <Dialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{t('timesheet.reopen.dialog_title')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            {t('timesheet.reopen.instructions')}
                        </p>
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('timesheet.reopen.label')}</Label>
                            <Textarea
                                value={reopenReason}
                                onChange={(e) => setReopenReason(e.target.value)}
                                placeholder={t('timesheet.reopen.placeholder')}
                                rows={4}
                                data-testid="reopen-reason-textarea"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReopenDialogOpen(false)}>{t('common.cancel')}</Button>
                        <Button onClick={handleRequestReopen} disabled={requestingReopen || !reopenReason.trim()}
                            data-testid="submit-reopen-btn">
                            {requestingReopen ? t('common.processing') : t('timesheet.reopen.submit_button')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Expense Reopen Request Dialog */}
            <Dialog open={expenseReopenDialogOpen} onOpenChange={setExpenseReopenDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{t('timesheet.reopen.dialog_title')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            {t('timesheet.reopen.instructions')}
                        </p>
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('timesheet.reopen.label')}</Label>
                            <Textarea
                                value={expenseReopenReason}
                                onChange={(e) => setExpenseReopenReason(e.target.value)}
                                placeholder={t('timesheet.reopen.placeholder')}
                                rows={4}
                                data-testid="expense-reopen-reason-textarea"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExpenseReopenDialogOpen(false)}>{t('common.cancel')}</Button>
                        <Button onClick={handleRequestExpenseReopen} disabled={requestingExpenseReopen || !expenseReopenReason.trim()}
                            data-testid="submit-expense-reopen-btn">
                            {requestingExpenseReopen ? t('common.processing') : t('timesheet.reopen.submit_button')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Task 1.R.4 — Bulk delete dialog */}
            <Dialog open={bulkDeleteOpen} onOpenChange={(open) => {
                if (!open) {
                    setBulkDeleteOpen(false);
                    setBulkDeleteCategories({ time: false, mileage: false, receipt: false, allowance: false });
                }
            }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('timesheet.bulk_delete.dialog_title', { count: selectedDays.length })}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            {t('timesheet.bulk_delete.instructions')}
                        </p>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    id="bulk-delete-time"
                                    checked={bulkDeleteCategories.time}
                                    onCheckedChange={(checked) =>
                                        setBulkDeleteCategories(prev => ({ ...prev, time: !!checked }))
                                    }
                                />
                                <label htmlFor="bulk-delete-time" className="text-sm cursor-pointer select-none">
                                    {t('timesheet.bulk_delete.time_entries_label')}
                                </label>
                            </div>
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    id="bulk-delete-mileage"
                                    checked={bulkDeleteCategories.mileage}
                                    onCheckedChange={(checked) =>
                                        setBulkDeleteCategories(prev => ({ ...prev, mileage: !!checked }))
                                    }
                                />
                                <label htmlFor="bulk-delete-mileage" className="text-sm cursor-pointer select-none">
                                    {t('timesheet.bulk_delete.mileage_label')}
                                </label>
                            </div>
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    id="bulk-delete-receipt"
                                    checked={bulkDeleteCategories.receipt}
                                    onCheckedChange={(checked) =>
                                        setBulkDeleteCategories(prev => ({ ...prev, receipt: !!checked }))
                                    }
                                />
                                <label htmlFor="bulk-delete-receipt" className="text-sm cursor-pointer select-none">
                                    {t('timesheet.bulk_delete.receipts_label')}
                                </label>
                            </div>
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    id="bulk-delete-allowance"
                                    checked={bulkDeleteCategories.allowance}
                                    onCheckedChange={(checked) =>
                                        setBulkDeleteCategories(prev => ({ ...prev, allowance: !!checked }))
                                    }
                                />
                                <label htmlFor="bulk-delete-allowance" className="text-sm cursor-pointer select-none">
                                    {t('timesheet.bulk_delete.allowances_label')}
                                </label>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="flex-col gap-2 sm:flex-row">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setBulkDeleteOpen(false);
                                setBulkDeleteCategories({ time: false, mileage: false, receipt: false, allowance: false });
                            }}
                            disabled={bulkDeleting}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleBulkDelete}
                            disabled={!Object.values(bulkDeleteCategories).some(Boolean) || bulkDeleting}
                            data-testid="confirm-bulk-delete-btn"
                        >
                            <Trash2 className="w-4 h-4 mr-1" />
                            {bulkDeleting ? t('common.deleting') : t('common.delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Task 1.R.1-R — Unified tabbed dialog (single-day flow) */}
            <UnifiedAddDialog
                open={unifiedAddOpen}
                onOpenChange={(next) => {
                    setUnifiedAddOpen(next);
                    if (!next) setQuickCaptureFile(null);
                }}
                date={unifiedAddDay?.date || null}
                dayObj={unifiedAddDay}
                projects={projects}
                deviationTypes={deviationTypes}
                dayEntries={dayEntries}
                setDayEntries={setDayEntries}
                userBalances={userBalances}
                lastProjectId={lastProjectId}
                setLastProjectId={setLastProjectId}
                existingExpenses={expenses}
                onSuccess={() => fetchData()}
                onTimeSaved={() => {
                    fetchData();
                    fetchUserBalances();
                }}
                initialTab={unifiedAddInitialTab}
                initialReceiptFile={quickCaptureFile}
                intlRateUrl={intlRateUrl}
                onNavigateDay={handleNavigateDay}
                onDirtyChange={setDialogDirty}
                isTimesheetLocked={isTimesheetLocked}
                isExpenseLocked={isExpenseLocked}
                weekLocked={unifiedAddDay ? weekLocks[String(getWeekNumber(new Date(unifiedAddDay.date)))] === true : false}
            />

            {/* Bulk mode — same tabbed dialog as single-day add (Task 1.R2 Round 2);
                selection is left intact after a save so the user can keep adding
                other entry types to the same days without re-selecting. */}
            <UnifiedAddDialog
                open={unifiedBulkOpen}
                onOpenChange={setUnifiedBulkOpen}
                dates={selectedDays}
                projects={projects}
                deviationTypes={deviationTypes}
                dayEntries={bulkTimeEntry}
                setDayEntries={setBulkTimeEntry}
                userBalances={userBalances}
                lastProjectId={lastProjectId}
                setLastProjectId={setLastProjectId}
                existingExpenses={expenses}
                intlRateUrl={intlRateUrl}
                onSuccess={() => {
                    fetchData();
                    setUnifiedBulkOpen(false);
                }}
                onTimeSaved={() => {
                    fetchData();
                    fetchUserBalances();
                    setUnifiedBulkOpen(false);
                }}
                isTimesheetLocked={isTimesheetLocked}
                isExpenseLocked={isExpenseLocked}
                weekLocked={selectedDays.some(d => weekLocks[String(getWeekNumber(new Date(d)))] === true)}
            />
        </div>
    );
}
